import { writable } from "svelte/store";
import { browser } from "$app/environment";
import {
  US_COUNTRIES,
  UK_COUNTRIES,
  EU_COUNTRIES,
  AU_COUNTRIES,
} from "#lib/constants.js";

// Store for the user's selected/detected country
export const userCountryStore = writable(null);

// Store for whether the country selection popup should be shown
export const showCountryPopupStore = writable(false);

// Store for the originally detected country (before user override)
export const detectedCountryStore = writable(null);

// Store for whether the user has manually selected a country (overriding detection)
export const hasManualCountryStore = writable(false);

/**
 * Check if user has already selected a country or if this is their first visit
 * @returns {boolean} Whether to show the country selection popup
 */
export function shouldShowCountryPopup() {
  if (!browser) return false;

  // Manual selection persists across sessions (localStorage)
  const hasManualSelection = localStorage.getItem("manuallySelectedCountry");
  // Auto-detected or popup-seen are per-session (sessionStorage)
  const hasSeenPopup = sessionStorage.getItem("hasSeenCountryPopup");

  // Only show on shop page
  const isShopPage = window.location.pathname === "/products/the-glorb/";

  // Show popup if user hasn't manually selected, hasn't seen popup this session, AND is on shop page
  return !hasManualSelection && !hasSeenPopup && isShopPage;
}

/**
 * The popup was closed without a country we can ship to: the visitor we cannot
 * serve, who left an email or simply looked. Mark it seen for the session so
 * it does not reopen on every page, and keep whatever country we had.
 */
export function dismissCountryPopup() {
  if (browser) sessionStorage.setItem("hasSeenCountryPopup", "true");
  showCountryPopupStore.set(false);
}

export function checkPopupOnPageChange() {
  if (shouldShowCountryPopup()) {
    showCountryPopupStore.set(true);
  }
}

/**
 * Determine which plug region a country belongs to
 * @param {string} countryCode - Two-letter country code
 * @returns {string} Plug region type
 */
export function getPlugRegion(countryCode) {
  if (US_COUNTRIES.includes(countryCode)) return "US";
  if (UK_COUNTRIES.includes(countryCode)) return "UK";
  if (EU_COUNTRIES.includes(countryCode)) return "EU";
  if (AU_COUNTRIES.includes(countryCode)) return "AU";
  return "UNKNOWN";
}

/**
 * Save user's country selection and mark popup as seen
 * @param {string} countryCode - Two-letter country code
 * @param {boolean} isManualSelection - Whether this was manually selected by user
 */
export function saveCountrySelection(countryCode, isManualSelection = false) {
  if (!browser) return;

  // Get current country and new country regions
  const currentCountry = getUserCountryPreference();
  const currentRegion = currentCountry ? getPlugRegion(currentCountry) : null;
  const newRegion = getPlugRegion(countryCode);

  // Check if we're switching between different plug regions
  const isRegionChange =
    currentRegion &&
    currentRegion !== newRegion &&
    currentRegion !== "UNKNOWN" &&
    newRegion !== "UNKNOWN";

  // Only an actual region (plug-type) change invalidates the cart - a manual
  // selection of the SAME region (e.g. re-confirming your country via the
  // "Free shipping to X" link) must not wipe the customer's cart.
  if (isRegionChange) {
    // console.log(`Region change detected: ${currentRegion} -> ${newRegion}. Clearing cart.`);
    // Clear the cart by removing cart ID - this will force a new cart creation
    localStorage.removeItem("cart_id");

    // Dispatch a custom event to notify components that cart should be cleared
    if (window.dispatchEvent) {
      window.dispatchEvent(
        new CustomEvent("countryRegionChanged", {
          detail: {
            from: currentRegion,
            to: newRegion,
            fromCountry: currentCountry,
            toCountry: countryCode,
          },
        }),
      );
    }
  }

  if (isManualSelection) {
    // Manual selection persists across sessions
    localStorage.setItem("manuallySelectedCountry", countryCode);
    hasManualCountryStore.set(true);
  } else {
    // Auto-detected country is per-session only
    sessionStorage.setItem("detectedCountry", countryCode);
  }

  // Popup seen flag is per-session - fresh detection each new session
  sessionStorage.setItem("hasSeenCountryPopup", "true");

  // Update stores
  userCountryStore.set(countryCode);
  showCountryPopupStore.set(false);

  // Log country change for debugging
  // console.log('🌍 Country changed:', {
  //   newCountry: countryCode,
  //   previousCountry: currentCountry,
  //   regionChange: isRegionChange,
  //   timestamp: new Date().toISOString()
  // });
}

/**
 * Get the user's country preference
 * Priority: Manual selection > Stored selection > Detected country > Default
 * @returns {string|null} Country code or null if none found
 */
export function getUserCountryPreference() {
  if (!browser) return null;

  // Manual selection (localStorage) - persists across sessions
  const manualSelection = localStorage.getItem("manuallySelectedCountry");
  if (manualSelection) {
    hasManualCountryStore.set(true);
    return manualSelection;
  }

  // Auto-detected country (sessionStorage) - fresh each session
  const detected = sessionStorage.getItem("detectedCountry");
  if (detected) return detected;

  return null;
}

/**
 * Reset country selection (for testing or user preference reset)
 */
export function resetCountrySelection() {
  if (!browser) return;

  localStorage.removeItem("manuallySelectedCountry");
  sessionStorage.removeItem("detectedCountry");
  sessionStorage.removeItem("hasSeenCountryPopup");

  userCountryStore.set(null);
  hasManualCountryStore.set(false);
  showCountryPopupStore.set(false);
}

// Initialize stores with current values when in browser
if (browser) {
  const currentCountry = getUserCountryPreference();
  if (currentCountry) {
    userCountryStore.set(currentCountry);
  }

  const hasManual = localStorage.getItem("manuallySelectedCountry");
  hasManualCountryStore.set(!!hasManual);
}
